import { Modal } from 'react-bootstrap'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import useApi from '../../../hooks/useApi'
import Button from '../../../components/button'
import Icon from '../../../components/icon'
import StripeSetupForm from '../../../components/StripeSetupForm'
import styles from '../../events/sponsorship/sponsorship.module.scss'
import { formatAmount } from '../../../lib/numbers'
import { Pledge } from '../../../types'

interface UpdatePaymentMethodModalProps {
  pledge?: Pledge
  onClose: () => void
  show: boolean
  onRefresh: () => void
}

type Step = 'form' | 'discard' | 'success'

export default function UpdatePaymentMethodModal({
  pledge,
  onClose,
  show,
  onRefresh,
}: UpdatePaymentMethodModalProps) {
  const { t } = useTranslation()
  const { updatePledgePaymentMethod } = useApi()

  const [step, setStep] = useState<Step>('form')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [retryInitiated, setRetryInitiated] = useState(false)

  const onPaymentMethodSuccess = useCallback(
    async (paymentMethodId: string) => {
      setLoading(true)
      setError(null)

      const pledgeId = pledge?.id
      if (pledgeId == null) {
        setError(t('dashboard.sponsorship.updatePaymentMethod.error'))
        setLoading(false)
        return
      }

      try {
        const response: any = await updatePledgePaymentMethod(
          pledgeId,
          paymentMethodId,
        )

        if (response?.error) {
          const errMsg =
            response?.error?.response?.data?.error ||
            response?.error?.message ||
            t('dashboard.sponsorship.updatePaymentMethod.error')
          setError(errMsg)
          return
        }

        setRetryInitiated(response?.data?.retry_initiated ?? false)
        setStep('success')
      } catch (err: any) {
        const errMsg =
          err?.message || t('dashboard.sponsorship.updatePaymentMethod.error')
        setError(errMsg)
      } finally {
        setLoading(false)
      }
    },
    [pledge?.id, updatePledgePaymentMethod, t],
  )

  const closeModal = useCallback((shouldRefresh = true) => {
    setStep('form')
    setError(null)
    setRetryInitiated(false)
    if (shouldRefresh) {
      onRefresh()
    }
    onClose()
  }, [onClose, onRefresh])

  const handleOnClose = useCallback(() => {
    if (step === 'success') {
      closeModal(true)
    } else if (step === 'discard') {
      // Already showing discard confirmation, do nothing
      return
    } else {
      setStep('discard')
    }
  }, [step, closeModal])

  if (!pledge) {
    return null
  }

  return (
    <Modal
      onHide={handleOnClose}
      show={show}
      backdrop={loading ? 'static' : true}
      keyboard={!loading}
      contentClassName={styles.modalBackground}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {step === 'form' && t('dashboard.sponsorship.updatePaymentMethod.title')}
          {step === 'discard' && t('dashboard.sponsorship.updatePaymentMethod.discardTitle')}
          {step === 'success' && t('dashboard.sponsorship.updatePaymentMethod.successTitle')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className={styles.modalBody}>
        {step === 'form' && (
          <div className={styles.section}>
            <div className="mb-3">
              <p className="text-muted">
                {t('dashboard.sponsorship.updatePaymentMethod.description')}
              </p>
            </div>

            <div className="mb-3 p-3 bg-light rounded">
              <div className="d-flex justify-content-between">
                <span>{t('dashboard.sponsorship.updatePaymentMethod.event')}</span>
                <span className="fw-bold">{pledge.event?.title}</span>
              </div>
              <div className="d-flex justify-content-between mt-2">
                <span>{t('dashboard.sponsorship.updatePaymentMethod.amount')}</span>
                <span className="fw-bold">
                  {formatAmount(pledge.amount)} CIV
                </span>
              </div>
              {pledge.payment_method_last_four && (
                <div className="d-flex justify-content-between mt-2">
                  <span>
                    {t('dashboard.sponsorship.updatePaymentMethod.currentMethod')}
                  </span>
                  <span className="text-danger">
                    {t(`dashboard.sponsorship.updatePaymentMethod.methodType.${pledge.payment_method_type}`)}{' '}
                    {t('dashboard.sponsorship.updatePaymentMethod.endingIn', { lastFour: pledge.payment_method_last_four })}
                  </span>
                </div>
              )}
            </div>

            {error && (
              <div className="alert alert-danger mb-3">{error}</div>
            )}

            <StripeSetupForm
              onSuccess={onPaymentMethodSuccess}
              onError={err => setError(err)}
              onCancel={() => closeModal(false)}
            />
          </div>
        )}

        {step === 'discard' && (
          <div className={styles.section}>
            <div className="mb-4">
              <p>{t('dashboard.sponsorship.updatePaymentMethod.discardMessage')}</p>
            </div>

            <div className="d-flex gap-2">
              <Button
                variant="outline-secondary"
                onClick={() => setStep('form')}
                className="flex-fill"
              >
                {t('dashboard.sponsorship.updatePaymentMethod.keepEditing')}
              </Button>
              <Button
                variant="danger"
                onClick={() => closeModal(false)}
                className="flex-fill"
              >
                {t('dashboard.sponsorship.updatePaymentMethod.discard')}
              </Button>
            </div>
          </div>
        )}

        {step === 'success' && (
          <div className={styles.section}>
            <div className={styles.successIconContainer}>
              <Icon name="check-circle" />
            </div>

            <div className="mb-4 text-center">
              {retryInitiated
                ? t('dashboard.sponsorship.updatePaymentMethod.successRetryMessage')
                : t('dashboard.sponsorship.updatePaymentMethod.successMessage')}
            </div>

            <div className="mb-3">
              <Button onClick={closeModal} className="w-100 mb-4">
                {t('buttons.close')}
              </Button>
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  )
}
